import { Menu, Transition } from '@headlessui/react'
import { Fragment, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useOnlineStatus } from '@/hooks/useOnlineStatus'
import { useToast } from '@/components/providers/ToastProvider'
import { useAuthStateSnapshot } from '@/lib/authStatus'
import { auth0Config } from '@/config/auth0'
import { Avatar } from '@/components/ui/Avatar'
import { APP_VERSION } from '@/config/version'

export function UserMenu(): JSX.Element {
  const { logout } = useAuth0()
  const { isAuthenticated, userName, userEmail } = useAuthStateSnapshot()
  const online = useOnlineStatus()
  const { showToast } = useToast()
  const [isSigningOut, setIsSigningOut] = useState(false)

  const triggerLabel = userName ? `Account menu for ${userName}` : 'Account menu'

  const handleLogout = async (): Promise<void> => {
    if (isSigningOut) return
    setIsSigningOut(true)
    try {
      await logout({
        logoutParams: {
          returnTo: auth0Config.logoutUri,
        },
        openUrl: online ? undefined : false,
      })
      if (!online) {
        showToast('Signed out locally. Connect to the internet to finish logging out of Auth0.', 'info')
      }
    } catch (error) {
      console.error('Failed to logout', error)
      showToast('Unable to logout. Try again in a moment.', 'error')
    } finally {
      setIsSigningOut(false)
    }
  }

  return (
    <Menu as="div" className="relative inline-flex">
      <Menu.Button
        type="button"
        className="logout-button flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-navy-light transition-colors hover:bg-navy-light/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-neon-cyan focus-visible:ring-offset-2 focus-visible:ring-offset-navy"
        aria-label={triggerLabel}
      >
        <Avatar name={userName} size="sm" />
      </Menu.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-120"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-90"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 z-50 mt-2 w-64 origin-top-right rounded-xl border border-white/15 bg-navy-light p-2 text-white shadow-xl focus:outline-none">
          <div className="px-3 py-2">
            <p className="truncate text-sm font-semibold">{userName ?? 'Signed in'}</p>
            {userEmail && <p className="truncate text-xs text-white/70">{userEmail}</p>}
          </div>
          <div className="my-2 border-t border-white/10" />

          <div className="space-y-1">
            <Menu.Item disabled={!isAuthenticated || isSigningOut}>
              {({ active, disabled }) => (
                <button
                  type="button"
                  onClick={handleLogout}
                  disabled={disabled}
                  className={`w-full rounded-lg px-3 py-2 text-left text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 ${
                    active && !disabled ? 'bg-white/10' : ''
                  }`}
                >
                  {isSigningOut ? 'Signing out…' : 'Sign out'}
                </button>
              )}
            </Menu.Item>
          </div>

          <div className="my-2 border-t border-white/10" />
          <p className="px-3 py-1.5 text-xs text-gray-500">Version {APP_VERSION}</p>
        </Menu.Items>
      </Transition>
    </Menu>
  )
}
